import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, Card, Paper, styled, useMediaQuery } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Box } from "@mui/system";
import CurrentBalanceTile from "./components/CurrentBalanceTile";
import TransactionHistory from "./components/TransactionHistory";
import RecurringPayments from "./components/RecurringPaymentsTile";
import PaymentMethodsTile from "./components/PaymentMethodsTile";
import Error500 from "../../icons/505Error.svg";
import { getBalanceDetails, getPaymentConfigs, getPaymentMethods, getAutoPays, getTransactionHistory } from "./sagas/residentpaymentsSlice";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import { Location } from "history";
import { RouteComponentProps } from "react-router-dom";
import { StaticContext } from "react-router";
import { ReactElement } from "react";
import { selectAuthState } from "../../shared/sagas/auth/authSlice";

type LocationState = {
  from: Location;
  tokenFromQuery: any;
  addprofile: boolean;
};

export default function Payments(props: RouteComponentProps<{}, StaticContext, LocationState>): ReactElement {
  const useStyles = makeStyles({
    gridContainer: {
      background: "#FFFFFF",
      boxShadow: "none !important",
    },
  });
  const classes = useStyles();
  const matchesMobile = useMediaQuery("(max-width:600px)");
  const [isAutoPaysFailed, setIsAutoPaysFailed] = React.useState(false);
  const [isBalanceFailed, setIsBalanceFailed] = React.useState(false);
  const [isPaymentMethodsFailed, setIsPaymentMethodsFailed] = React.useState(false);
  const [openConfirmationDialog, setOpenConfirmationDialog] = React.useState(false);
  const handleAutoPaysFailure = () => setIsAutoPaysFailed(true);
  const handleBalanceFailure = () => setIsBalanceFailed(true);
  const handlePaymentMethodsFailure = () => setIsPaymentMethodsFailed(true);
  const { isFetching, isUpdateUserDetailsSuccess} = useSelector(selectAuthState);
  const dispatch = useDispatch();
  const { location } = props;

  React.useEffect(() => {
     if(isUpdateUserDetailsSuccess){
      dispatch(getBalanceDetails());
      dispatch(getPaymentConfigs());
      dispatch(getPaymentMethods());
      dispatch(getAutoPays());
      dispatch(getTransactionHistory());
     }
  }, [dispatch,isUpdateUserDetailsSuccess]);

  const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: "center",
    color: theme.palette.text.secondary,
  }));

  const confirmationText = `When you log in you will be directed to this account.
    If you wish to view a different account, use the drop down in the upper right.`;

  React.useEffect(() => {
    if (location && location?.state?.addprofile) {
      setOpenConfirmationDialog(true);
    }
  }, [location.state]);

  return (
    <Box>
      {isFetching ? ''
       : isAutoPaysFailed === true || isBalanceFailed === true || isPaymentMethodsFailed === true ? (
        <Grid container direction="row" spacing={2} alignItems="stretch">
          <Grid item xs={12}>
            <img src={Error500} alt="500" />
          </Grid>
        </Grid>
      ) : (
        <Grid container direction="row" spacing={2} alignItems="stretch">
          <Grid item xs={12} lg={6} xl={3}>
            <Item>
              <Card
                sx={{
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: matchesMobile ? 150 : 240,
                  maxHeight: 240,
                }}
                className={classes.gridContainer}
              >
                <CurrentBalanceTile handleFailure={handleBalanceFailure} />
              </Card>
            </Item>
          </Grid>
          <Grid item xs={12} lg={6} xl={3}>
            <Item>
              <Card
                sx={{
                  p: 1,
                  display: "flex",
                  flexDirection: "column",
                  height: 240,
                }}
                className={classes.gridContainer}
              >
                <RecurringPayments handleFailure={handleAutoPaysFailure} />
              </Card>
            </Item>
          </Grid>
          <Grid item xs={12} xl={6}>
            <Item>
              <Card
                sx={{
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  height: 240,
                }}
                className={classes.gridContainer}
              >
                <PaymentMethodsTile handleFailure={handlePaymentMethodsFailure} />
              </Card>
            </Item>
          </Grid>
          <Grid item xs={12}>
            <Item>
              <Card sx={{ p: 1, display: "flex", flexDirection: "column" }} className={classes.gridContainer}>
                <TransactionHistory />
              </Card>
            </Item>
          </Grid>
        </Grid>
      )}

      <ConfirmationDialog
        title="Account Successfully Added"
        buttonText="Got It!"
        open={openConfirmationDialog}
        handleClose={() => {
          setOpenConfirmationDialog(false);
        }}
        text={confirmationText}
      />
    </Box>
  );
}
